// src/hooks/usePurchaseOrderForm.js
import { useState, useEffect, useCallback } from 'react';
import { getUserWarehouses } from '../services/api/purchaseOrder';
import { getImageUrl } from '../utils/imageUtils';
import { getAllSuppliers } from '../services/api/supplier';
import { getProcurementPaymentMethods } from '../services/api/procurement/supplierPayment';
import { calculatePaymentBalance } from '../utils/orderCalculations';
import { useLocalization } from '../contexts/LocalizationContext';

const usePurchaseOrderForm = (initialData = null) => {
  const { t } = useLocalization();
  // Form state
  const [formData, setFormData] = useState({
    warehouseId: null,
    supplierId: null,
    orderDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    items: [],
    extraCharges: '',
    extraChargesDescription: '',
    discountAmount: '',
    discountDescription: '',
    notes: '',
    status: 'pending',
    autoReceive: false,
    paymentRecords: []
  });

  // Reference data
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [totals, setTotals] = useState({
    subtotal: 0,
    extraCharges: 0,
    discountAmount: 0,
    grandTotal: 0
  });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        
        const [warehousesResponse, suppliersResponse, paymentMethodsResponse] = await Promise.all([
          getUserWarehouses(),
          getAllSuppliers({ limit: 100, isActive: true }),
          getProcurementPaymentMethods().catch(() => [])
        ]);
        
        setWarehouses(warehousesResponse.warehouses || []);
        setSuppliers(suppliersResponse.suppliers || []);
        setPaymentMethods(Array.isArray(paymentMethodsResponse) ? paymentMethodsResponse : []);
        
        // Set initial data if provided (for edit mode)
        if (initialData) {
          setFormData({
            warehouseId: initialData.warehouseId,
            supplierId: initialData.supplierId,
            orderDate: initialData.orderDate,
            expectedDeliveryDate: initialData.expectedDeliveryDate || '',
            items: initialData.items || [],
            extraCharges: initialData.extraCharges || '',
            extraChargesDescription: initialData.extraChargesDescription || '',
            discountAmount: initialData.discountAmount || '',
            discountDescription: initialData.discountDescription || '',
            notes: initialData.notes || '',
            status: initialData.status || 'pending',
            autoReceive: initialData.autoReceive ?? false,
            paymentRecords: initialData.paymentRecords || []
          });
        }

        const warehouseList = warehousesResponse.warehouses || [];
        if (warehouseList.length === 1) {
          setFormData(prev => (prev.warehouseId ? prev : { ...prev, warehouseId: warehouseList[0].id }));
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, [initialData]);

  // Calculate totals when items or charges change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => {
      return sum + (parseFloat(item.lineTotal) || 0);
    }, 0);

    const extraCharges = parseFloat(formData.extraCharges) || 0;
    const discountAmount = parseFloat(formData.discountAmount) || 0;
    const grandTotal = subtotal + extraCharges - discountAmount;

    setTotals({
      subtotal,
      extraCharges,
      discountAmount,
      grandTotal: Math.max(0, grandTotal) // Ensure non-negative
    });
  }, [formData.items, formData.extraCharges, formData.discountAmount]);

  // Handle form field changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  // Handle item changes
  const addItem = useCallback((product) => {
    const newItem = {
      id: Date.now(),
      productId: product.productId,
      variationId: product.variationId || null,
      displayName: product.displayName,
      sku: product.sku,
      imageUrl: product.image ? getImageUrl(product.image) : null,
      quantity: 1,
      unitPrice: product.price ?? '',
      lineTotal: product.price || 0,
      notes: ''
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Clear items error if exists
    if (errors.items) {
      setErrors(prev => ({ ...prev, items: '' }));
    }
  }, [errors.items]);

  const updateItem = useCallback((itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Recalculate line total if quantity or unit price changed
          if (field === 'quantity' || field === 'unitPrice') {
            const quantity = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(item.quantity) || 0;
            const unitPrice = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(item.unitPrice) || 0;
            updatedItem.lineTotal = quantity * unitPrice;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  }, []);

  const removeItem = useCallback((itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  }, []);

  // Handle payment records (mirrors useOrderForm)
  const addPaymentRecord = useCallback((paymentMethodKey) => {
    setFormData(prev => {
      const balance = calculatePaymentBalance(totals.grandTotal, prev.paymentRecords);
      const newRecord = {
        id: `new-${Date.now()}`,
        isNew: true,
        paymentMethodKey: paymentMethodKey || '',
        paymentAmount: balance.isFullyPaid ? '' : balance.balance,
        reference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        proofUrl: null
      };
      return {
        ...prev,
        paymentRecords: [...prev.paymentRecords, newRecord]
      };
    });
  }, [totals.grandTotal]);

  const updatePaymentRecord = useCallback((recordId, field, value) => {
    setFormData(prev => ({
      ...prev,
      paymentRecords: prev.paymentRecords.map(record =>
        record.id === recordId ? { ...record, [field]: value } : record
      )
    }));
  }, []);

  const removePaymentRecord = useCallback((recordId) => {
    setFormData(prev => ({
      ...prev,
      paymentRecords: prev.paymentRecords.filter(record => record.id !== recordId)
    }));
  }, []);

  // Payload shape for the backend: debit-note offsets are managed server-side, never sent
  const getPaymentRecordsPayload = useCallback(() => (
    formData.paymentRecords
      .filter(record => !record.isDebitNote)
      .map(record => ({
        ...(record.isNew ? {} : { id: record.id }),
        paymentMethodKey: record.paymentMethodKey,
        paymentAmount: parseFloat(record.paymentAmount),
        reference: record.reference || '',
        proofUrl: record.proofUrl || null,
        paymentDate: record.paymentDate
      }))
  ), [formData.paymentRecords]);

  // Add supplier to list (after creating new one)
  const addSupplier = useCallback((supplier) => {
    setSuppliers(prev => [supplier, ...prev]);
    setFormData(prev => ({ ...prev, supplierId: supplier.id }));
  }, []);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Required fields
    if (!formData.warehouseId) {
      newErrors.warehouseId = 'Warehouse is required';
    }
    
    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }
    
    if (!formData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }
    
    // Items validation
    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      const itemErrors = [];
      formData.items.forEach((item, index) => {
        const itemError = {};
        
        const quantity = Number(item.quantity);
        if (item.quantity === '' || !quantity || quantity <= 0) {
          itemError.quantity = t('purchaseOrder.quantityMustBeGreaterThanZero');
        } else if (!Number.isInteger(quantity)) {
          itemError.quantity = t('purchaseOrder.quantityMustBeWholeNumber');
        }

        if (item.unitPrice === '' || item.unitPrice === null || Number(item.unitPrice) < 0) {
          itemError.unitPrice = t('purchaseOrder.unitPriceRequired');
        }
        
        if (Object.keys(itemError).length > 0) {
          itemErrors[index] = itemError;
        }
      });
      
      if (itemErrors.length > 0) {
        newErrors.itemDetails = itemErrors;
      }
    }
    
    // Financial validation
    if (formData.extraCharges && parseFloat(formData.extraCharges) < 0) {
      newErrors.extraCharges = 'Extra charges cannot be negative';
    }
    
    if (formData.discountAmount && parseFloat(formData.discountAmount) < 0) {
      newErrors.discountAmount = 'Discount amount cannot be negative';
    }

    // Payment records validation (mirrors useOrderForm)
    if (formData.paymentRecords.length > 0) {
      const recordErrors = [];
      formData.paymentRecords.forEach((record, index) => {
        if (record.isDebitNote) return;
        const recordError = {};
        if (!record.paymentMethodKey) recordError.paymentMethodKey = 'Payment method is required';
        if (!record.paymentAmount || record.paymentAmount <= 0) recordError.paymentAmount = 'Payment amount must be greater than 0';
        if (!record.paymentDate) recordError.paymentDate = 'Payment date is required';
        if (Object.keys(recordError).length > 0) recordErrors[index] = recordError;
      });
      if (recordErrors.length > 0) newErrors.paymentRecordDetails = recordErrors;

      if (calculatePaymentBalance(totals.grandTotal, formData.paymentRecords).isOverpaid) {
        newErrors.paymentRecords = 'Total payments cannot exceed the purchase order grand total';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, totals.grandTotal, t]);

  return {
    // State
    formData,
    warehouses,
    suppliers,
    paymentMethods,
    isLoading,
    errors,
    totals,
    
    // Handlers
    handleFieldChange,
    addItem,
    updateItem,
    removeItem,
    addSupplier,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    
    // Utilities
    validateForm,
    getPaymentRecordsPayload,
    
    // Setters for direct manipulation
    setFormData,
    setErrors
  };
};

export default usePurchaseOrderForm;